import React, { useEffect, useState } from 'react';
import { ShieldCheck, Package } from 'lucide-react';
import { fetchBatches, fetchInboundBatch } from '../../lib/api';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * Inbound Purity Verified — per-batch ledger of every raw-material drum
 * whose tamper-seal was scanned and broken on the Atelier floor. Surfaced
 * publicly on /transparency so guests can audit "what arrived, when, from
 * whom" for the batch behind their bottle.
 */
const InboundPurityLedger = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchBatches()
      .then(async (b) => {
        const batches = b.batches || [];
        const enriched = await Promise.all(
          batches.map(async (batch) => {
            try {
              const inbound = await fetchInboundBatch(batch.id);
              return { batch, drums: inbound.drums || [] };
            } catch {
              return { batch, drums: [] };
            }
          })
        );
        if (alive) setRows(enriched.filter((r) => r.drums.length > 0));
      })
      .catch(() => {
        if (alive) setRows([]);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="inbound-ledger-loading"
        className="h-48 bg-[#C1D7E2]/40 animate-pulse"
      />
    );
  }
  if (rows.length === 0) return null;

  return (
    <section
      id="inbound-purity-ledger"
      data-testid="inbound-purity-ledger"
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
    >
      <div className="max-w-[1300px] mx-auto">
        <SectionLabel testId="inbound-ledger-label">
          Inbound Purity Verified
        </SectionLabel>
        <h3 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-4 max-w-3xl">
          Every drum that fed your bottle —{' '}
          <em className="text-[#90B0BF]">scanned at the door.</em>
        </h3>
        <p className="text-[#7C9CB1]/85 font-light leading-relaxed text-lg max-w-2xl mb-12">
          When a raw-material drum arrives at the Atelier, its tamper-seal is
          verified on chain. Only verified drums may enter a batch. The exact
          moment of opening is timestamped below.
        </p>

        <div className="space-y-10">
          {rows.map((r, i) => (
            <BatchCard key={r.batch.id} index={i} batch={r.batch} drums={r.drums} />
          ))}
        </div>
      </div>
    </section>
  );
};

const BatchCard = ({ batch, drums, index }) => (
  <article
    data-testid={`inbound-batch-${batch.id}`}
    className="border border-[#C1D7E2] bg-white/50 backdrop-blur-md p-6 md:p-8"
  >
    <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b border-[#C1D7E2]/60 pb-5 mb-6">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
        Batch · {batch.id}
      </p>
      <h4 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] leading-tight">
        {batch.name}
      </h4>
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
        {drums.length} drum{drums.length === 1 ? '' : 's'} verified
      </span>
    </header>

    <ul className="space-y-4">
      {drums.map((d, i) => (
        <li
          key={d.drum_id}
          data-testid={`inbound-batch-${batch.id}-drum-${i}`}
          className="grid grid-cols-12 gap-3 items-start"
        >
          <div className="col-span-1 pt-1">
            <ShieldCheck size={18} strokeWidth={1.3} className="text-[#7C9CB1]" />
          </div>
          <div className="col-span-11 md:col-span-6">
            <p className="font-serif text-lg text-[#7C9CB1]">
              <Package size={14} className="inline mr-2 -mt-0.5" />
              {d.ingredient_name}
            </p>
            <p className="text-xs text-[#90B0BF] italic mt-0.5">
              {d.supplier} · {d.origin}
            </p>
          </div>
          <div className="col-start-2 md:col-start-auto col-span-11 md:col-span-5 md:text-right space-y-1">
            <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#7C9CB1]">
              Inbound Purity Verified · {d.opened_at}
            </p>
            <p className="font-mono text-[10px] tracking-[0.18em] text-[#90B0BF]">
              {d.drum_id} · {d.chain_hash}
            </p>
          </div>
        </li>
      ))}
    </ul>
  </article>
);

export default InboundPurityLedger;
